import { cn } from "@/lib/utils";
import { forwardRef } from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "gold" | "dark" | "outline" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", loading, children, disabled, ...props }, ref) => {
    const base = "inline-flex items-center justify-center gap-2 font-medium transition-all rounded-[var(--sf-radius)] cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed";

    const variants = {
      primary: "bg-[var(--sf-primary)] text-[var(--sf-card-btn-text,#ffffff)] hover:bg-[var(--sf-primary-hover)]",
      gold: "bg-[var(--sf-accent)] text-white uppercase tracking-widest text-xs font-[family-name:var(--font-rubik)] hover:bg-[var(--sf-accent-hover)] hover:-translate-y-px hover:shadow-[0_6px_20px_rgba(212,175,55,0.35)]",
      dark: "bg-[var(--sf-primary)] text-[var(--sf-card-btn-text,#ffffff)] uppercase tracking-widest text-xs font-[family-name:var(--font-rubik)] hover:bg-[var(--sf-primary-hover)] hover:-translate-y-px",
      outline: "border border-[var(--sf-primary)] text-[var(--sf-primary)] bg-transparent hover:bg-gray-100",
      ghost: "bg-transparent text-gray-600 hover:bg-gray-100",
      danger: "bg-red-600 text-white hover:bg-red-700",
    };

    const sizes = {
      sm: "h-8 px-3 text-xs",
      md: "h-10 px-4 text-sm",
      lg: "h-12 px-6 text-base",
    };

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(base, variants[variant], sizes[size], className)}
        {...props}
      >
        {loading && (
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        )}
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";
export { Button };
